/**
 * @file AuthContext.js
 * @description Define el contexto de autenticación para la aplicación React.
 * Provee el estado de autenticación del usuario, funciones de login y logout,
 * y persiste los tokens JWT en el almacenamiento local.
 * Incluye una lógica de inicialización robusta para manejar la expiración de tokens en la carga.
 */
import React, { createContext, useState, useEffect, useMemo, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'react-bootstrap';
import api, { setupInterceptors } from '../services/api';
import { rolePermissions } from '../utils/permissions';
import { identifyUser, resetAnalytics } from '../services/analytics';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const { i18n } = useTranslation();
    const [authTokens, setAuthTokens] = useState(() =>
        localStorage.getItem('authTokens') ? JSON.parse(localStorage.getItem('authTokens')) : null
    );
    const [user, setUser] = useState(null);
    const [business, setBusiness] = useState(null);
    const [businessSettings, setBusinessSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isImpersonating, setIsImpersonating] = useState(() => !!localStorage.getItem('adminAuthTokens'));
    const interceptorIds = React.useRef(null);
    const currentPermissionsRef = React.useRef(null);

    const updateAuthAndUser = useCallback((newTokens, effectivePermissions = null) => {
        const decodedUser = jwtDecode(newTokens.token);
        // Priority: explicit permissions (from DB fetch) > previously loaded permissions (on token refresh) > role defaults
        decodedUser.permissions = effectivePermissions ?? currentPermissionsRef.current ?? rolePermissions[decodedUser.role] ?? [];
        currentPermissionsRef.current = decodedUser.permissions;

        setAuthTokens(newTokens);
        setUser(decodedUser);
        localStorage.setItem('authTokens', JSON.stringify(newTokens));
        api.defaults.headers.common['Authorization'] = `Bearer ${newTokens.token}`;
    }, []);

    const loadEffectivePermissions = useCallback(async (tokens) => {
        try {
            const response = await api.get('/role-permissions/effective', {
                headers: { Authorization: `Bearer ${tokens.token}` }
            });
            return response.data.permissions || null;
        } catch {
            return null;
        }
    }, []);

    const logoutUser = useCallback(async () => {
        if (authTokens) {
            try {
                // If checking for admin tokens here might be redundant for normal logout, 
                // but if we are impersonating and click logout, we might want to just stop impersonating?
                // For now, full logout.
                if (isImpersonating) {
                    // Logic to clear admin tokens too? Or just clear everything.
                    localStorage.removeItem('adminAuthTokens');
                    setIsImpersonating(false);
                }

                // SEC-10b: logout selectivo — se manda el refresh token de ESTA sesión para
                // revocar solo este dispositivo (no las demás sesiones del usuario).
                await api.post('/auth/logout', { refreshToken: authTokens.refreshToken });
            } catch (error) {
                console.error("Backend logout failed", error);
            }
        }

        setAuthTokens(null);
        setUser(null);
        setBusiness(null);
        setBusinessSettings(null);
        currentPermissionsRef.current = null;
        localStorage.removeItem('authTokens');
        // Ensure admin token is cleared on full logout
        localStorage.removeItem('adminAuthTokens');
        setIsImpersonating(false);

        // PostHog: limpia la identidad para no atribuir la próxima sesión a este usuario.
        resetAnalytics();

        api.defaults.headers.common['Authorization'] = null;

        if (interceptorIds.current) {
            api.interceptors.request.eject(interceptorIds.current.requestInterceptor);
            api.interceptors.response.eject(interceptorIds.current.responseInterceptor);
        }
        interceptorIds.current = setupInterceptors({ logoutUser: () => { } });

    }, [authTokens, isImpersonating]);

    // Tarea 027c — todo lo que hay que hacer una vez se TIENEN los tokens (decodificar,
    // guardar sesión, reinstalar interceptores, cargar negocio/settings/idioma). Extraído
    // de loginUser para que RegisterPage pueda aplicar los tokens que ya devuelve
    // POST /auth/register directamente, sin volver a pasar por /auth/login.
    const applyAuthTokens = useCallback(async (newAuthTokens) => {
        const effectivePermissions = await loadEffectivePermissions(newAuthTokens);
        updateAuthAndUser(newAuthTokens, effectivePermissions);

        if (interceptorIds.current) {
            api.interceptors.request.eject(interceptorIds.current.requestInterceptor);
            api.interceptors.response.eject(interceptorIds.current.responseInterceptor);
        }
        interceptorIds.current = setupInterceptors({ authTokens: newAuthTokens, logoutUser, updateAuthAndUser });

        const decodedUser = jwtDecode(newAuthTokens.token);
        if (decodedUser.business_id) {
            try {
                const businessResponse = await api.get(`/businesses/${decodedUser.business_id}`);
                if (businessResponse.data) setBusiness(businessResponse.data);

                if ((rolePermissions[decodedUser.role] || []).includes('view_business_settings')) {
                    const settingsResponse = await api.get('/settings');
                    if (settingsResponse.data) {
                        setBusinessSettings(settingsResponse.data);
                        // Language precedence logic
                        const userLang = decodedUser.language;
                        const businessLang = settingsResponse.data.language;
                        i18n.changeLanguage(userLang || businessLang || 'en');
                    }
                } else {
                    // Fallback for users without business settings permission
                    i18n.changeLanguage(decodedUser.language || 'en');
                }
            } catch (err) {
                console.error("Error fetching initial data", err);
            }
        } else {
            i18n.changeLanguage(decodedUser.language || 'en');
        }
    }, [logoutUser, updateAuthAndUser, loadEffectivePermissions, i18n]);

    const loginUser = useCallback(async (email, password, rememberMe) => {
        const response = await api.post('/auth/login', { email, password, rememberMe });
        if (response.data) {
            await applyAuthTokens(response.data);
        }
        return response;
    }, [applyAuthTokens]);

    const refreshBusiness = useCallback(async () => {
        if (user && user.business_id) {
            try {
                const businessResponse = await api.get(`/businesses/${user.business_id}`);
                if (businessResponse.data) {
                    setBusiness(businessResponse.data);
                }
            } catch (error) {
                console.error("Failed to refresh business data", error);
            }
        }
    }, [user]);

    const impersonateUser = useCallback(async (businessId) => {
        try {
            const response = await api.post(`/admin/businesses/${businessId}/impersonate`);
            const { token } = response.data;
            const newTokens = { token }; // No refresh token for impersonation

            // Save current admin session
            if (!isImpersonating) {
                localStorage.setItem('adminAuthTokens', JSON.stringify(authTokens));
                setIsImpersonating(true);
            }

            // Update to new session
            updateAuthAndUser(newTokens);

            // Reload basic business data for new user
            const decodedUser = jwtDecode(token);
            const businessResponse = await api.get(`/businesses/${decodedUser.business_id}`);
            if (businessResponse.data) setBusiness(businessResponse.data);

            // Note: We don't need to manually reload settings, user effect or page load will handle
            toast.success("Viendo como Negocio");

            // Force reload to clean state if needed, or react router navigation
            window.location.href = '/dashboard';

        } catch (error) {
            console.error("Impersonation failed", error);
            toast.error("Error al iniciar sesión como negocio");
            throw error;
        }
    }, [authTokens, isImpersonating, updateAuthAndUser]);

    const stopImpersonating = useCallback(() => {
        const storedAdminTokens = localStorage.getItem('adminAuthTokens');
        if (storedAdminTokens) {
            const adminTokens = JSON.parse(storedAdminTokens);
            updateAuthAndUser(adminTokens); // Restore admin session

            localStorage.removeItem('adminAuthTokens');
            setIsImpersonating(false);

            toast.success("Sesión de Super Admin restaurada");
            window.location.href = '/admin/businesses';
        } else {
            // Cannot restore? Logout?
            logoutUser();
        }
    }, [updateAuthAndUser, logoutUser]);

    const switchBusinessHandler = useCallback(async (targetBusinessId) => {
        try {
            const response = await api.post('/auth/switch-business', { target_business_id: targetBusinessId });
            // SEC-10 (Fase 5): switch-business ya no reemite un refresh token (la sesión no
            // cambia al cambiar de negocio activo) — se conserva el que ya teníamos.
            const newAuthTokens = { token: response.data.token, refreshToken: authTokens?.refreshToken };
            updateAuthAndUser(newAuthTokens);

            // Reload business and settings data for the new business
            const decodedUser = jwtDecode(newAuthTokens.token);
            try {
                const businessResponse = await api.get(`/businesses/${decodedUser.business_id}`);
                if (businessResponse.data) setBusiness(businessResponse.data);

                if ((rolePermissions[decodedUser.role] || []).includes('view_business_settings')) {
                    const settingsResponse = await api.get('/settings');
                    if (settingsResponse.data) {
                        setBusinessSettings(settingsResponse.data);
                        const userLang = decodedUser.language;
                        const businessLang = settingsResponse.data.language;
                        i18n.changeLanguage(userLang || businessLang || 'en');
                    }
                } else {
                    i18n.changeLanguage(decodedUser.language || 'en');
                }
            } catch (err) {
                console.error("Error fetching business data after switch", err);
            }

            toast.success("Negocio cambiado exitosamente");
            window.location.href = '/dashboard'; // Force reload to clean state

        } catch (error) {
            console.error("Business switch failed", error);
            toast.error("Error al cambiar de negocio");
            throw error;
        }
    }, [authTokens, updateAuthAndUser, i18n]);

    useEffect(() => {
        const initializeAuth = async () => {
            const storedTokens = authTokens;

            if (storedTokens) {
                try {
                    const decodedToken = jwtDecode(storedTokens.token);
                    const currentTime = Date.now() / 1000;
                    let tokensToUse = storedTokens;

                    // Only try to refresh if it's NOT an impersonation session (which has no refresh token usually)
                    // Or if we check isImpersonating state.
                    const isImpersonatingCheck = !!localStorage.getItem('adminAuthTokens');
                    setIsImpersonating(isImpersonatingCheck);

                    if (decodedToken.exp < currentTime) {
                        if (isImpersonatingCheck) {
                            // Expired impersonation session -> Restore admin
                            console.log("Impersonation session expired. Restoring admin.");
                            const storedAdminTokens = localStorage.getItem('adminAuthTokens');
                            if (storedAdminTokens) {
                                tokensToUse = JSON.parse(storedAdminTokens);
                                localStorage.removeItem('adminAuthTokens');
                                setIsImpersonating(false);
                                // Don't refresh here immediately, let next loop or logic handle if admin token also expired
                            } else {
                                throw new Error("Impersonation expired and no admin token");
                            }
                        } else {
                            console.log("Access token expired, attempting to refresh...");
                            const response = await api.post('/auth/refresh', {
                                refreshToken: storedTokens.refreshToken
                            });

                            tokensToUse = {
                                token: response.data.token,
                                // SEC-10b: guardar el refresh token rotado que devuelve el backend.
                                refreshToken: response.data.refreshToken || storedTokens.refreshToken
                            };
                        }
                    } else {
                        console.log("Access token is valid.");
                    }

                    const effectivePermissions = await loadEffectivePermissions(tokensToUse);
                    updateAuthAndUser(tokensToUse, effectivePermissions);

                    const decodedUser = jwtDecode(tokensToUse.token);
                    if (decodedUser.business_id) {
                        try {
                            const businessResponse = await api.get(`/businesses/${decodedUser.business_id}`);
                            if (businessResponse.data) setBusiness(businessResponse.data);

                            if ((rolePermissions[decodedUser.role] || []).includes('view_business_settings')) {
                                const settingsResponse = await api.get('/settings');
                                if (settingsResponse.data) {
                                    setBusinessSettings(settingsResponse.data);
                                    // Language precedence logic
                                    const userLang = decodedUser.language;
                                    const businessLang = settingsResponse.data.language;
                                    i18n.changeLanguage(userLang || businessLang || 'en');
                                }
                            } else {
                                i18n.changeLanguage(decodedUser.language || 'en');
                            }
                        } catch (err) {
                            console.warn('Initial data fetch warning:', err);
                        }
                    } else {
                        i18n.changeLanguage(decodedUser.language || 'en');
                    }

                } catch (error) {
                    console.error("Auth initialization failed. Logging out.", error);
                    logoutUser();
                }
            }

            setLoading(false);
        };

        interceptorIds.current = setupInterceptors({ authTokens, logoutUser, updateAuthAndUser });

        initializeAuth();

        return () => {
            if (interceptorIds.current) {
                api.interceptors.request.eject(interceptorIds.current.requestInterceptor);
                api.interceptors.response.eject(interceptorIds.current.responseInterceptor);
            }
        };
    }, []);

    // PostHog: atribuye los eventos al usuario + negocio (multi-tenancy). Se dispara en login,
    // restauración de sesión y cambio de negocio. Si aún no hay consentimiento, PostHog suprime
    // el envío (opt-out por defecto). En impersonación NO se identifica, para no contaminar la
    // analítica del negocio con sesiones de superadmin.
    useEffect(() => {
        if (user && !isImpersonating) {
            identifyUser(user, business);
        }
    }, [user, business, isImpersonating]);

    const contextData = useMemo(() => ({
        user,
        authTokens,
        business,
        businessSettings,
        loginUser,
        logoutUser,
        setBusiness,
        setUser,
        updateAuthTokens: updateAuthAndUser, // Exporting the function
        applyAuthTokens, // Tarea 027c — RegisterPage lo usa para el auto-login tras registrarse
        refreshBusiness,
        impersonateUser,
        stopImpersonating,
        isImpersonating,
        switchBusiness: switchBusinessHandler
    }), [user, authTokens, business, businessSettings, loginUser, logoutUser, updateAuthAndUser, applyAuthTokens, refreshBusiness, impersonateUser, stopImpersonating, isImpersonating, switchBusinessHandler]);

    return (
        <AuthContext.Provider value={contextData}>
            {loading
                ? <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}><Spinner animation="border" /></div>
                : children
            }
        </AuthContext.Provider>
    );
};

export default AuthContext;
